import { createFileRoute } from "@tanstack/react-router";
import { requireAdmin } from "@/lib/admin-guard";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  ExternalLink,
} from "lucide-react";
import { toast } from "sonner";

import {
  adminListEvents,
  adminUpsertEvent,
  adminDeleteEvent,
  type AdminEvent,
} from "@/lib/events.functions";

export const Route = createFileRoute("/admin_/events-calendar")({
  ssr: false,
  head: () => ({
    meta: [
      { title: "Events Calendar — Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: requireAdmin,
  component: EventsCalendarAdmin,
});

type FormState = {
  id?: string;
  slug: string;
  title: string;
  description: string;
  host: string;
  category: string;
  event_image_url: string;
  starts_at_date: string;
  starts_at_time: string;
  duration_minutes: number;
  status: "draft" | "published" | "completed";
  source: "internal" | "external";
  external_url: string;
  location: string;
};

const EMPTY_FORM: FormState = {
  slug: "",
  title: "",
  description: "",
  host: "",
  category: "community",
  event_image_url: "",
  starts_at_date: new Date().toISOString().slice(0, 10),
  starts_at_time: "18:00",
  duration_minutes: 60,
  status: "published",
  source: "external",
  external_url: "",
  location: "",
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 100);
}

function toLocalInput(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

function EventsCalendarAdmin() {
  const [filter, setFilter] = useState<"all" | "external" | "internal">("all");
  const [form, setForm] = useState<FormState | null>(null);

  const list = useServerFn(adminListEvents);
  const upsert = useServerFn(adminUpsertEvent);
  const del = useServerFn(adminDeleteEvent);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-events"],
    queryFn: () => list(),
  });

  const rows = useMemo(() => {
    const all = (data ?? []) as AdminEvent[];
    if (filter === "all") return all;
    return all.filter((e) => e.source === filter);
  }, [data, filter]);

  const saveMut = useMutation({
    mutationFn: async (f: FormState) => {
      const startsAt = new Date(`${f.starts_at_date}T${f.starts_at_time}`).toISOString();
      return upsert({
        data: {
          id: f.id,
          slug: f.slug || slugify(f.title),
          title: f.title,
          description: f.description,
          host: f.host,
          category: f.category || "community",
          event_image_url: f.event_image_url || null,
          starts_at: startsAt,
          duration_minutes: f.duration_minutes,
          status: f.status,
          source: f.source,
          external_url: f.source === "external" ? f.external_url || null : null,
          location: f.location || null,
        },
      });
    },
    onSuccess: () => {
      toast.success("Event saved");
      setForm(null);
      qc.invalidateQueries({ queryKey: ["admin-events"] });
    },
    onError: (e: Error) => toast.error(e.message ?? "Save failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      toast.success("Event deleted");
      qc.invalidateQueries({ queryKey: ["admin-events"] });
    },
    onError: (e: Error) => toast.error(e.message ?? "Delete failed"),
  });

  function editEvent(e: AdminEvent) {
    const { date, time } = toLocalInput(e.starts_at);
    setForm({
      id: e.id,
      slug: e.slug,
      title: e.title,
      description: e.description ?? "",
      host: e.host ?? "",
      category: e.category ?? "community",
      event_image_url: e.event_image_url ?? "",
      starts_at_date: date,
      starts_at_time: time,
      duration_minutes: e.duration_minutes,
      status: e.status as FormState["status"],
      source: (e.source ?? "internal") as FormState["source"],
      external_url: e.external_url ?? "",
      location: e.location ?? "",
    });
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl text-foreground">Events Calendar — Admin</h1>
          <p className="text-sm text-muted-foreground">
            Add and manage external events (conferences, webinars, awareness days) alongside
            Recovery Trauma™ events.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setForm({ ...EMPTY_FORM })}
          className="btn-gold inline-flex items-center gap-1.5 text-sm"
        >
          <Plus className="h-4 w-4" /> Add event
        </button>
      </header>

      <div className="mb-4 inline-flex overflow-hidden rounded-full border border-gold/30 text-xs">
        {(["all", "external", "internal"] as const).map((v) => (
          <button
            key={v}
            type="button"
            onClick={() => setFilter(v)}
            className={`px-3 py-1.5 font-medium capitalize ${
              filter === v
                ? "bg-[var(--gold-deep)] text-white"
                : "bg-background text-foreground hover:bg-gold/10"
            }`}
          >
            {v === "internal" ? "Recovery Trauma™" : v}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 rounded-2xl border border-dashed border-gold/40 bg-cream/40 p-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading events…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gold/40 bg-cream/40 p-8 text-center text-sm text-muted-foreground">
          No events yet.
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-gold/30 bg-card">
          <table className="w-full text-sm">
            <thead className="bg-gold/10 text-start text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">When</th>
                <th className="px-3 py-2">Source</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} className="border-t border-gold/15">
                  <td className="px-3 py-2">
                    <div className="font-medium text-foreground">{e.title}</div>
                    <div className="text-xs text-muted-foreground">
                      {e.location ?? (e.source === "external" ? "See organiser" : "Online (Zoom)")}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {new Date(e.starts_at).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
                        e.source === "external"
                          ? "bg-sky-100 text-sky-800"
                          : "bg-emerald-100 text-emerald-800"
                      }`}
                    >
                      {e.source === "external" ? "External" : "Recovery Trauma™"}
                    </span>
                    {e.source === "external" && e.external_url ? (
                      <a
                        href={e.external_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ms-2 inline-flex items-center gap-0.5 text-[var(--gold-deep)] hover:underline"
                      >
                        link <ExternalLink className="h-3 w-3" />
                      </a>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-xs capitalize">{e.status}</td>
                  <td className="px-3 py-2 text-end">
                    <button
                      type="button"
                      onClick={() => editEvent(e)}
                      className="me-2 inline-flex items-center gap-1 rounded-full border border-gold/30 px-2 py-1 text-xs hover:bg-gold/10"
                    >
                      <Pencil className="h-3 w-3" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm(`Delete "${e.title}"?`)) delMut.mutate(e.id);
                      }}
                      className="inline-flex items-center gap-1 rounded-full border border-rose-300 px-2 py-1 text-xs text-rose-700 hover:bg-rose-50"
                    >
                      <Trash2 className="h-3 w-3" /> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form ? (
        <EventFormModal
          form={form}
          setForm={setForm}
          saving={saveMut.isPending}
          onCancel={() => setForm(null)}
          onSave={() => saveMut.mutate(form)}
        />
      ) : null}
    </div>
  );
}

function EventFormModal({
  form,
  setForm,
  saving,
  onCancel,
  onSave,
}: {
  form: FormState;
  setForm: (f: FormState) => void;
  saving: boolean;
  onCancel: () => void;
  onSave: () => void;
}) {
  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm({ ...form, [k]: v });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="mt-8 w-full max-w-2xl rounded-3xl border border-gold/30 bg-card p-6 shadow-xl">
        <h2 className="font-display text-xl text-foreground">
          {form.id ? "Edit event" : "Add event"}
        </h2>

        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Type</span>
            <select
              value={form.source}
              onChange={(e) => set("source", e.target.value as FormState["source"])}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
            >
              <option value="external">External</option>
              <option value="internal">Recovery Trauma™ (internal)</option>
            </select>
          </label>

          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Title *</span>
            <input
              value={form.title}
              onChange={(e) => {
                set("title", e.target.value);
                if (!form.id && !form.slug) set("slug", slugify(e.target.value));
              }}
              onBlur={() => {
                if (!form.slug) set("slug", slugify(form.title));
              }}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
              required
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Slug *</span>
            <input
              value={form.slug}
              onChange={(e) => set("slug", slugify(e.target.value))}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
              required
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Host / Organiser</span>
            <input
              value={form.host}
              onChange={(e) => set("host", e.target.value)}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Date *</span>
            <input
              type="date"
              value={form.starts_at_date}
              onChange={(e) => set("starts_at_date", e.target.value)}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
              required
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Time *</span>
            <input
              type="time"
              value={form.starts_at_time}
              onChange={(e) => set("starts_at_time", e.target.value)}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
              required
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Duration (min)</span>
            <input
              type="number"
              min={5}
              max={600}
              value={form.duration_minutes}
              onChange={(e) => set("duration_minutes", parseInt(e.target.value || "60", 10))}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Location</span>
            <input
              value={form.location}
              onChange={(e) => set("location", e.target.value)}
              placeholder="Online, London, etc."
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
            />
          </label>

          {form.source === "external" ? (
            <label className="text-sm md:col-span-2">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">
                Registration URL *
              </span>
              <input
                type="url"
                value={form.external_url}
                onChange={(e) => set("external_url", e.target.value)}
                placeholder="https://…"
                className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
                required
              />
            </label>
          ) : null}

          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Image URL (optional)</span>
            <input
              type="url"
              value={form.event_image_url}
              onChange={(e) => set("event_image_url", e.target.value)}
              placeholder="https://…"
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
            />
          </label>

          <label className="text-sm md:col-span-2">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Description</span>
            <textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={4}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
            />
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Status</span>
            <select
              value={form.status}
              onChange={(e) => set("status", e.target.value as FormState["status"])}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
            >
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="completed">Completed</option>
            </select>
          </label>

          <label className="text-sm">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Category</span>
            <input
              value={form.category}
              onChange={(e) => set("category", e.target.value)}
              className="w-full rounded-xl border border-gold/30 bg-background px-3 py-2"
            />
          </label>
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-full border border-gold/30 px-4 py-2 text-sm hover:bg-gold/10"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={saving || !form.title || !form.starts_at_date}
            className="btn-gold inline-flex items-center gap-1.5 text-sm disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Save event
          </button>
        </div>
      </div>
    </div>
  );
}
